import React, { useEffect, useRef, useState } from 'react';
import { FaCircle, FaUndo } from 'react-icons/fa';
import api from '../../utils/api';
import meetingRecorder from '../../utils/meetingRecorder';
import { AutomaticRecordingController, getAutomaticRecordingAttemptId, automaticRecordingConfigured, automaticRecordingEnabled,
  canAttemptAutomaticRecording, canManageAutomaticRecording } from '../../utils/automaticRecording';
import './automatic-recording.css';
import { recordingUiError } from '../../utils/recordingControls';

export default function AutomaticRecordingControl({ room, role, participantStatus, sessionId, recording, onStarted }) {
  const context = { room, role, participantStatus, sessionId };
  const contextRef = useRef(context);
  contextRef.current = context;
  const startedRef = useRef(onStarted);
  startedRef.current = onStarted;
  const controllerRef = useRef(null);
  const [snapshot, setSnapshot] = useState({ policy: null, busy: false, error: '', countdownAttempted: false });
  const [showControls, setShowControls] = useState(false);
  useEffect(() => {
    const show = () => setShowControls(true);
    const escape = event => { if (event.key === 'Escape') setShowControls(false); };
    window.addEventListener('meeting-recording-options-open', show);
    window.addEventListener('keydown', escape);
    return () => { window.removeEventListener('meeting-recording-options-open', show); window.removeEventListener('keydown', escape); };
  }, []);
  const configured = automaticRecordingConfigured(room);
  const eligible = canAttemptAutomaticRecording(context);
  const configuredEnabled = automaticRecordingEnabled(room);
  useEffect(() => {
    if (!configured || !eligible) return undefined;
    setSnapshot({ policy: null, busy: false, error: '', countdownAttempted: false });
    let attemptId;
    try { attemptId = getAutomaticRecordingAttemptId({ crypto: window.crypto, storage: window.sessionStorage, roomId: room.roomId, sessionId }); }
    catch (error) { setSnapshot({ policy: null, error: error.message, busy: false }); return undefined; }
    const controller = new AutomaticRecordingController({ api, recorder: meetingRecorder,
      readContext: () => contextRef.current, onChange: setSnapshot, onStarted: () => startedRef.current?.(),
      attemptId });
    controllerRef.current = controller;
    void controller.tick();
    const poll = window.setInterval(() => void controller.tick(), 5000);
    const startRequested = async event => {
      if (String(event.detail?.roomId || '') !== String(room.roomId) || controller.busy) return;
      if (controller.manualStopped || ['completed', 'skipped'].includes(controller.policy?.state) || !automaticRecordingEnabled(contextRef.current.room)) {
        try { await meetingRecorder.unlockRecordingAudio(); await controller.decision('record'); }
        catch (error) { controller.captureFailed(error.message); }
        finally { meetingRecorder.releasePrimedAudio(); }
      } else await controller.confirm();
      if (controller.error) setShowControls(true);
    };
    window.addEventListener('meeting-recording-start-click', startRequested);
    const failed = (event) => {
      if (String(event.detail?.roomId || '') === String(room.roomId)) {
        controller.captureFailed(event.detail?.message);
      }
    };
    const updated = (event) => {
      if (String(event.detail?.roomId || '') === String(room.roomId)) void controller.tick();
    };
    const stopped = event => {
      if (String(event.detail?.roomId || '') === String(room.roomId)) controller.captureStopped();
    };
    const settled = event => {
      if (String(event.detail?.roomId || '') === String(room.roomId)) controller.stopSettled(event.detail);
    };
    window.addEventListener('meeting-recording-policy-updated', updated);
    window.addEventListener('meeting-recording-capture-error', failed);
    window.addEventListener('meeting-recording-manual-stop', stopped);
    window.addEventListener('meeting-recording-stop-settled', settled);
    return () => { controller.dispose(); window.clearInterval(poll);
      window.removeEventListener('meeting-recording-start-click', startRequested);
      window.removeEventListener('meeting-recording-policy-updated', updated);
      window.removeEventListener('meeting-recording-capture-error', failed);
      window.removeEventListener('meeting-recording-manual-stop', stopped);
      window.removeEventListener('meeting-recording-stop-settled', settled);
      controllerRef.current = null; };
  }, [room?.roomId, sessionId, configured, configuredEnabled, eligible]);
  if (!configured || !eligible || !showControls) return null;
  const policy = snapshot.policy;
  const saving = snapshot.manualStopped && policy?.state === 'stopping';
  const retrySave = snapshot.manualStopped && policy?.state === 'paused';
  const isActive = !saving && !retrySave && (recording || policy?.state === 'recording');
  const enabled = policy ? policy.enabled : automaticRecordingEnabled(room);
  const canCancel = canManageAutomaticRecording(role) && (policy?.canCancel !== false);
  const ownLease = policy?.sessionId === sessionId;
  const needsConfirmation = !snapshot.manualStopped && !isActive && enabled && snapshot.error && ownLease;
  const confirm = () => { void controllerRef.current?.confirm(); };
  const stop = () => window.dispatchEvent(new CustomEvent('meeting-recording-stop-click', { detail: { roomId: room.roomId } }));
  return <>
    <aside className="automatic-recording-status" aria-label="Automatic recording status">
      <button type="button" onClick={() => setShowControls(false)} aria-label="Close recording options">✕</button>
      <FaCircle className={isActive ? 'automatic-recording-live' : ''} aria-hidden="true" />
      <span role="status">{saving ? 'Saving recording…' : retrySave ? 'Recording save needs a retry' : isActive ? 'Recording' : policy?.state === 'completed' ? 'Recording saved' : enabled ? ownLease ? 'Preparing recording' : 'Waiting for recording host' : 'Automatic recording paused'}</span>
      {(isActive || retrySave) && <button type="button" onClick={stop} disabled={snapshot.busy} aria-label={retrySave ? 'Retry recording save' : 'Stop recording'}>{retrySave ? 'Retry save' : 'Stop recording'}</button>}
      {needsConfirmation && <button type="button" onClick={confirm} disabled={snapshot.busy}>Confirm recording</button>}
      {canCancel && !isActive && !saving && !retrySave && policy?.state === 'completed' && <button type="button" disabled={snapshot.busy}
        onClick={() => { void controllerRef.current?.decision('record'); }}>Start recording</button>}
      {canCancel && !isActive && !saving && !retrySave && policy?.state !== 'completed' && <button type="button" disabled={snapshot.busy}
        onClick={() => { void controllerRef.current?.decision(enabled ? 'skip' : 'record'); }}>
        {enabled ? 'Pause requirement' : <><FaUndo aria-hidden="true" /> Enable again</>}
      </button>}
    </aside>
    {snapshot.error && <p className="automatic-recording-connection-error" role="status">{recordingUiError({ message: snapshot.error })}</p>}
  </>;
}
